import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Loader2,
  CreditCard,
  DollarSign,
  AlertTriangle,
  CheckCircle,
  XCircle,
  RefreshCcw,
  Plus,
  Settings,
  ExternalLink,
  Mail,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

const STATUS_CONFIG = {
  pending: { label: 'Ожидает', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400', icon: RefreshCcw },
  processing: { label: 'Обработка', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400', icon: Loader2 },
  succeeded: { label: 'Оплачен', color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400', icon: CheckCircle },
  failed: { label: 'Ошибка', color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400', icon: XCircle },
  refunded: { label: 'Возврат', color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400', icon: RefreshCcw },
  cancelled: { label: 'Отменён', color: 'bg-slate-100 text-slate-700 dark:bg-slate-900/30 dark:text-slate-400', icon: XCircle },
};

function formatDateTime(isoString) {
  if (!isoString) return '-';
  const date = new Date(isoString);
  return date.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });
}

function formatAmount(amount, currency) {
  return new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency: currency || 'USD'
  }).format(amount);
}

function PaymentStatusBadge({ status }) {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  const Icon = config.icon;
  return (
    <Badge className={cn("text-xs flex items-center gap-1", config.color)}>
      <Icon className="h-3 w-3" />
      {config.label}
    </Badge>
  );
}

export default function PaymentsPage() {
  const { api, user } = useAuth();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [payments, setPayments] = useState([]);
  const [config, setConfig] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Create payment dialog
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [orders, setOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState('');
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentProvider, setPaymentProvider] = useState('stripe');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [paymentsRes, configRes] = await Promise.all([
        api.get(`/payments/intents?limit=100${statusFilter !== 'all' ? `&status=${statusFilter}` : ''}`),
        api.get('/payments/config')
      ]);
      setPayments(paymentsRes.data);
      setConfig(configRes.data);
      
      // Set default provider based on config
      if (configRes.data.default_provider) {
        setPaymentProvider(configRes.data.default_provider);
      }
    } catch (err) {
      console.error('Failed to fetch payments:', err);
    } finally {
      setLoading(false);
    }
  }, [api, statusFilter]);

  const fetchOrders = useCallback(async () => {
    try {
      const res = await api.get('/orders?limit=50&op_status=done');
      setOrders(res.data.filter(o => o.payment_status !== 'paid'));
    } catch (err) {
      console.error('Failed to fetch orders:', err);
    }
  }, [api]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreatePayment = async () => {
    if (!selectedOrder || !paymentAmount) {
      toast.error(t('payments.fillAllFields'));
      return;
    }

    setCreateLoading(true);
    try {
      const res = await api.post('/payments/intents', {
        order_id: selectedOrder,
        amount: parseFloat(paymentAmount),
        currency: 'USD',
        provider: paymentProvider
      });

      if (res.data.status === 'failed') {
        toast.error(res.data.error_message || t('payments.createFailed'));
      } else {
        toast.success(t('payments.created'));
        
        // If YooKassa, open confirmation URL
        if (res.data.confirmation_url) {
          window.open(res.data.confirmation_url, '_blank');
        }
      }
      
      setCreateDialogOpen(false);
      setSelectedOrder('');
      setPaymentAmount('');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('payments.createFailed'));
    } finally {
      setCreateLoading(false);
    }
  };

  const handleMarkPaid = async (paymentId) => {
    try {
      await api.post(`/payments/intents/${paymentId}/mark-paid`);
      toast.success(t('payments.markedPaid'));
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('payments.markPaidFailed'));
    }
  };

  const openCreateDialog = () => {
    fetchOrders();
    setCreateDialogOpen(true);
  };

  // Only admin and manager can access
  if (user?.role !== 'admin' && user?.role !== 'manager') {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <CreditCard className="h-16 w-16 mx-auto text-muted-foreground/30 mb-4" />
          <p className="text-muted-foreground">{t('payments.accessDenied')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-20">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-foreground">{t('payments.title')}</h1>
          <p className="text-muted-foreground mt-1">{t('payments.subtitle')}</p>
        </div>
        <Button onClick={openCreateDialog} data-testid="create-payment-btn">
          <Plus className="h-4 w-4 mr-2" />
          {t('payments.createPayment')}
        </Button>
      </div>

      {/* Configuration Status */}
      {config && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className={cn("cgc-card", config.stripe.configured ? "border-green-500/30" : "border-amber-500/30")}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={cn(
                    "w-10 h-10 rounded-lg flex items-center justify-center",
                    config.stripe.configured ? "bg-green-500/10 text-green-500" : "bg-amber-500/10 text-amber-500"
                  )}>
                    <CreditCard className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="font-semibold">Stripe</p>
                    <p className="text-xs text-muted-foreground">
                      {config.stripe.configured ? t('payments.configured') : t('payments.notConfigured')}
                    </p>
                  </div>
                </div>
                {config.stripe.configured ? (
                  <CheckCircle className="h-5 w-5 text-green-500" />
                ) : (
                  <AlertTriangle className="h-5 w-5 text-amber-500" />
                )}
              </div>
            </CardContent>
          </Card>

          <Card className={cn("cgc-card", config.yookassa.configured ? "border-green-500/30" : "border-amber-500/30")}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={cn(
                    "w-10 h-10 rounded-lg flex items-center justify-center",
                    config.yookassa.configured ? "bg-green-500/10 text-green-500" : "bg-amber-500/10 text-amber-500"
                  )}>
                    <DollarSign className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="font-semibold">YooKassa</p>
                    <p className="text-xs text-muted-foreground">
                      {config.yookassa.configured ? t('payments.configured') : t('payments.notConfigured')}
                    </p>
                  </div>
                </div>
                {config.yookassa.configured ? (
                  <CheckCircle className="h-5 w-5 text-green-500" />
                ) : (
                  <AlertTriangle className="h-5 w-5 text-amber-500" />
                )}
              </div>
            </CardContent>
          </Card>

          <Card className={cn("cgc-card", config.email?.email_enabled ? "border-green-500/30" : "border-slate-500/30")}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={cn(
                    "w-10 h-10 rounded-lg flex items-center justify-center",
                    config.email?.email_enabled ? "bg-green-500/10 text-green-500" : "bg-slate-500/10 text-slate-500"
                  )}>
                    <Mail className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="font-semibold">{t('payments.emailReceipts')}</p>
                    <p className="text-xs text-muted-foreground">
                      {config.email?.email_enabled 
                        ? `${config.email.active_provider?.toUpperCase()}`
                        : t('payments.notConfigured')}
                    </p>
                  </div>
                </div>
                {config.email?.email_enabled ? (
                  <CheckCircle className="h-5 w-5 text-green-500" />
                ) : (
                  <AlertTriangle className="h-5 w-5 text-slate-400" />
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Configuration Help */}
      {config && !config.stripe.configured && !config.yookassa.configured && (
        <Card className="cgc-card border-amber-500/30 bg-amber-50/50 dark:bg-amber-900/10">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Settings className="h-5 w-5 text-amber-500 mt-0.5" />
              <div>
                <p className="font-medium text-amber-700 dark:text-amber-400">{t('payments.setupRequired')}</p>
                <p className="text-sm text-muted-foreground mt-1">{t('payments.setupInstructions')}</p>
                <div className="mt-3 space-y-2 text-sm">
                  <p><strong>Stripe:</strong> {t('payments.stripeInstructions')}</p>
                  <p><strong>YooKassa:</strong> {t('payments.yookassaInstructions')}</p>
                  <p className="pt-2 border-t"><strong>{t('payments.emailSetup')}:</strong></p>
                  <p className="text-xs text-muted-foreground">
                    • Resend: RESEND_API_KEY<br/>
                    • SendGrid: SENDGRID_API_KEY<br/>
                    • SMTP: SMTP_HOST, SMTP_PORT, SMTP_USER, SMTP_PASSWORD<br/>
                    • EMAIL_PROVIDER = resend | sendgrid | smtp
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card className="cgc-card">
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">{t('common.status')}</Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[160px]" data-testid="payment-status-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('common.all')}</SelectItem>
                  <SelectItem value="pending">{t('payments.status.pending')}</SelectItem>
                  <SelectItem value="succeeded">{t('payments.status.succeeded')}</SelectItem>
                  <SelectItem value="failed">{t('payments.status.failed')}</SelectItem>
                  <SelectItem value="refunded">{t('payments.status.refunded')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Payments Table */}
      <Card className="cgc-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <CreditCard className="h-5 w-5" />
            {t('payments.history')}
            <Badge variant="outline" className="ml-2">{payments.length}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-accent" />
            </div>
          ) : payments.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <CreditCard className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p>{t('payments.noPayments')}</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('payments.date')}</TableHead>
                  <TableHead>{t('payments.order')}</TableHead>
                  <TableHead>{t('payments.amount')}</TableHead>
                  <TableHead>{t('payments.provider')}</TableHead>
                  <TableHead>{t('common.status')}</TableHead>
                  <TableHead className="text-right">{t('common.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {payments.map((payment) => (
                  <TableRow key={payment.id} data-testid={`payment-${payment.id}`}>
                    <TableCell className="font-mono text-sm">
                      {formatDateTime(payment.created_at)}
                    </TableCell>
                    <TableCell>
                      <span className="text-sm">{payment.order_id?.slice(0, 8)}...</span>
                    </TableCell>
                    <TableCell className="font-semibold">
                      {formatAmount(payment.amount, payment.currency)}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs">
                        {payment.provider === 'stripe' ? 'Stripe' : 
                         payment.provider === 'yookassa' ? 'YooKassa' : 
                         t('payments.manual')}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <PaymentStatusBadge status={payment.status} />
                    </TableCell>
                    <TableCell className="text-right">
                      {payment.status === 'pending' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleMarkPaid(payment.id)}
                          data-testid={`mark-paid-${payment.id}`}
                        >
                          <CheckCircle className="h-4 w-4 mr-1" />
                          {t('payments.markPaid')}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create Payment Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5" />
              {t('payments.createPayment')}
            </DialogTitle>
            <DialogDescription>
              {t('payments.createDescription')}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>{t('payments.selectOrder')}</Label>
              <Select value={selectedOrder} onValueChange={setSelectedOrder}>
                <SelectTrigger data-testid="select-order">
                  <SelectValue placeholder={t('payments.selectOrderPlaceholder')} />
                </SelectTrigger>
                <SelectContent>
                  {orders.map(order => (
                    <SelectItem key={order.id} value={order.id}>
                      {order.title} - {formatAmount(order.total_amount || 0, 'USD')}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>{t('payments.amount')}</Label>
              <Input
                type="number"
                value={paymentAmount}
                onChange={(e) => setPaymentAmount(e.target.value)}
                placeholder="0.00"
                data-testid="payment-amount"
              />
            </div>

            <div className="space-y-2">
              <Label>{t('payments.provider')}</Label>
              <Select value={paymentProvider} onValueChange={setPaymentProvider}>
                <SelectTrigger data-testid="select-provider">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="stripe" disabled={!config?.stripe?.configured}>
                    Stripe {!config?.stripe?.configured && `(${t('payments.notConfigured')})`}
                  </SelectItem>
                  <SelectItem value="yookassa" disabled={!config?.yookassa?.configured}>
                    YooKassa {!config?.yookassa?.configured && `(${t('payments.notConfigured')})`}
                  </SelectItem>
                  <SelectItem value="manual">{t('payments.manual')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleCreatePayment} disabled={createLoading}>
              {createLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('payments.create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
